import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Card, message, Spin, Modal, Upload } from 'antd';
import { SaveOutlined, PlusOutlined, ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { getCourse, updateCourse, createCourse } from './api';
import config from './config';

const { TextArea } = Input;
const { Option } = Select;

interface CourseFormProps {
  courseId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface CourseData {
  title: string;
  description?: string;
  shortDescription?: string;
  image_url?: string;
  status: 'draft' | 'published';
  aiAssistant?: 'gpt' | 'claude';
  dailyAiMessageLimit?: number | string;
  dailyTestLimit?: number | string;
  isPaid?: boolean;
  trialLessonsCount?: number;
}

const CourseForm: React.FC<CourseFormProps> = ({ courseId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
  const isEditing = !!courseId;

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    setInitialLoading(true);
    try {
      const course = await getCourse(courseId);
      console.log('Загруженные данные курса:', course);
      form.setFieldsValue({
        title: course.title,
        description: course.description || '',
        shortDescription: course.shortDescription || '',
        image_url: course.image_url || '',
        status: course.status || 'draft',
        aiAssistant: course.aiAssistant || 'gpt',
        dailyAiMessageLimit: course.dailyAiMessageLimit?.toString() || '30',
        dailyTestLimit: course.dailyTestLimit?.toString() || '10',
        isPaid: Boolean(course.isPaid),
        trialLessonsCount: course.trialLessonsCount ? Number(course.trialLessonsCount) : 0,
      });
      setImagePreview(course.image_url || undefined);
      setHasChanges(false);
    } catch (error) {
      message.error('Ошибка загрузки курса');
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  }, [courseId, form]);

    useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId, loadCourse]);

  const handleSubmit = async (values: CourseData) => {
    setLoading(true);
    try {
      // Конвертируем строки в числа для лимитов
      const processedValues = {
        ...values,
        dailyAiMessageLimit: values.dailyAiMessageLimit 
          ? parseInt(values.dailyAiMessageLimit.toString(), 10) 
          : undefined,
        dailyTestLimit: values.dailyTestLimit 
          ? parseInt(values.dailyTestLimit.toString(), 10) 
          : undefined,
        // Автоматически заменяем http:// на https:// для URL изображения
        image_url: values.image_url 
          ? values.image_url.replace(/^http:\/\//, 'https://')
          : values.image_url,
      };

      if (isEditing && courseId) {
        await updateCourse(courseId, processedValues);
        message.success('Курс успешно обновлен');
      } else {
        await createCourse(processedValues);
        message.success('Курс успешно создан');
      }
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Подтверждение',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
        okText: 'Да, выйти',
        cancelText: 'Отмена',
        onOk: () => {
          setHasChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const handleValuesChange = () => {
    setHasChanges(true);
  };

  // --- Загрузка изображения ---
  const handleImageUpload = async (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      const url = info.file.response?.url;
      if (url) {
        // Автоматически заменяем http:// на https:// для URL изображения
        const secureUrl = url.replace(/^http:\/\//, 'https://');
        form.setFieldsValue({ image_url: secureUrl });
        setImagePreview(secureUrl);
        setHasChanges(true);
        message.success('Изображение успешно загружено');
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error('Ошибка загрузки изображения');
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ padding: 0, height: 'auto' }}
          />
          {isEditing ? 'Редактировать курс' : 'Создать новый курс'}
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        initialValues={{
          status: 'draft',
          aiAssistant: 'gpt',
          dailyAiMessageLimit: '30',
          dailyTestLimit: '10',
          isPaid: false,
          trialLessonsCount: 0,
        }}
      >
        <Form.Item
          name="title"
          label="Название курса"
          rules={[{ required: true, message: 'Пожалуйста, введите название курса' }]}
        >
          <Input placeholder="Введите название курса" />
        </Form.Item>

        <Form.Item
          name="shortDescription"
          label="Короткое описание (до 255 символов)"
          rules={[{ max: 255, message: 'Максимум 255 символов' }]}
        >
          <TextArea rows={2} placeholder="Короткое описание курса" showCount maxLength={255} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
        >
          <TextArea 
            rows={4} 
            placeholder="Введите описание курса"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item label="Обложка курса">
          <Upload
            name="file"
            action={`${config.apiUrl}/courses/upload`}
            headers={{ Authorization: `Bearer ${localStorage.getItem('jwt') || ''}` }}
            showUploadList={false}
            onChange={handleImageUpload}
            accept="image/*"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              Загрузить изображение
            </Button>
          </Upload>
          {imagePreview && (
            <div style={{ marginTop: 12 }}>
              <img src={imagePreview} alt="Обложка" style={{ maxWidth: 200, borderRadius: 8, boxShadow: '0 2px 8px #eee' }} />
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="image_url"
          label="URL изображения"
        >
          <Input 
            placeholder="https://example.com/image.png" 
            onChange={(e) => {
              const value = e.target.value;
              if (value && value.startsWith('http://')) {
                const secureUrl = value.replace(/^http:\/\//, 'https://');
                form.setFieldsValue({ image_url: secureUrl });
                setImagePreview(secureUrl);
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Пожалуйста, выберите статус' }]}
        >
          <Select placeholder="Выберите статус">
            <Option value="draft">Черновик</Option>
            <Option value="published">Опубликован</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="aiAssistant"
          label="AI-ассистент"
          rules={[{ required: true, message: 'Пожалуйста, выберите AI-ассистента' }]}
        >
          <Select placeholder="Выберите AI-ассистента">
            <Option value="gpt">GPT (OpenAI)</Option>
            <Option value="claude">Claude (Anthropic)</Option>
          </Select>
        </Form.Item>

        {/* Секция лимитов */}
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '24px',
          backgroundColor: '#fafafa'
        }}>
          <h3 style={{ marginBottom: '16px', color: '#1890ff' }}>📊 Лимиты курса</h3>
          
          <Form.Item
            name="dailyAiMessageLimit"
            label="Дневной лимит AI сообщений"
            rules={[
              { required: true, message: 'Пожалуйста, введите лимит AI сообщений' },
              {
                validator: (_, value) => {
                  if (!value || value.toString().trim() === '') {
                    return Promise.reject(new Error('Пожалуйста, введите лимит AI сообщений'));
                  }
                  const numValue = parseInt(value.toString(), 10);
                  if (isNaN(numValue) || numValue < 1 || numValue > 1000) {
                    return Promise.reject(new Error('Лимит должен быть от 1 до 1000'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              placeholder="30" 
              addonAfter="сообщений/день"
            />
          </Form.Item>

          <Form.Item
            name="dailyTestLimit"
            label="Дневной лимит неудачных попыток тестов"
            rules={[
              { required: true, message: 'Пожалуйста, введите лимит тестов' },
              {
                validator: (_, value) => {
                  if (!value || value.toString().trim() === '') {
                    return Promise.reject(new Error('Пожалуйста, введите лимит тестов'));
                  }
                  const numValue = parseInt(value.toString(), 10);
                  if (isNaN(numValue) || numValue < 1 || numValue > 100) {
                    return Promise.reject(new Error('Лимит должен быть от 1 до 100'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              placeholder="10" 
              addonAfter="попыток/день"
            />
          </Form.Item>

          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            backgroundColor: '#fff', 
            padding: '8px 12px', 
            borderRadius: '4px',
            border: '1px solid #e8e8e8'
          }}>
            <strong>ℹ️ Как работают лимиты:</strong><br/>
            • <strong>AI сообщения:</strong> Количество сообщений с AI-ассистентом в день на пользователя<br/>
            • <strong>Тесты:</strong> Количество неудачных попыток прохождения тестов и открытых вопросов в день на пользователя<br/>
            • <strong>Успешные тесты</strong> не тратят лимит<br/>
            • Лимиты обновляются каждый день в полночь по времени сервера
          </div>
        </div>

        {/* Секция платных курсов */}
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '24px',
          backgroundColor: '#fafafa'
        }}>
          <h3 style={{ marginBottom: '16px', color: '#722ed1' }}>💰 Настройки платного курса</h3>
          
          <Form.Item
            name="isPaid"
            label="Платный курс"
          >
            <Select placeholder="Выберите тип курса">
              <Option value={false}>Бесплатный</Option>
              <Option value={true}>Платный</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="trialLessonsCount"
            label="Количество бесплатных уроков"
            rules={[
              {
                validator: (_, value) => {
                  const isPaid = form.getFieldValue('isPaid');
                  if (isPaid && (value === undefined || value === null || value === '')) {
                    return Promise.reject(new Error('Для платных курсов укажите количество бесплатных уроков'));
                  }
                  if (isPaid && (value < 0 || value > 100)) {
                    return Promise.reject(new Error('Количество бесплатных уроков должно быть от 0 до 100'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              placeholder="0" 
              addonAfter="уроков"
              type="number"
              min={0}
              max={100}
            />
          </Form.Item>

          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            backgroundColor: '#fff', 
            padding: '8px 12px', 
            borderRadius: '4px',
            border: '1px solid #e8e8e8'
          }}>
            <strong>ℹ️ Как работают платные курсы:</strong><br/>
            • <strong>Бесплатные курсы:</strong> Все уроки доступны без ограничений<br/>
            • <strong>Платные курсы:</strong> Первые N уроков доступны бесплатно, остальные требуют покупки<br/>
            • <strong>Бесплатные уроки:</strong> Пользователи могут изучить часть курса перед покупкой<br/>
            • <strong>Цены настраиваются</strong> отдельно в разделе "Цены курсов"
          </div>
        </div>

        <Form.Item>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Отмена
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CourseForm; 